import { RefObject, useEffect } from "react";

interface ScaleToParentOptions {
  enabled?: boolean;
  scaleFactor?: number;
  offsetPx?: number;
}

/**
 * A React hook that scales an element to its parent's width, with either a scale factor or pixel offset.
 * This is useful for fixed elements where we can't simply use w-full to scale to the parent's width.
 *
 * @param elementRef - A React ref to the element to be scaled.
 * @param options - An optional object with the following properties:
 *   - `enabled`: A boolean indicating whether the scaling should be enabled (default is `true`).
 *   - `scaleFactor`: A number representing the scale factor to apply (mutually exclusive with offsetPx)
 *   - `offsetPx`: Number of pixels to subtract from parent width (mutually exclusive with scaleFactor)
 * @returns Void
 */
export function useScaleToParent(elementRef: RefObject<HTMLElement>, options: ScaleToParentOptions = {}) {
  const { enabled = true, scaleFactor, offsetPx } = options;

  useEffect(() => {
    if (!enabled || !elementRef.current) return;

    const element = elementRef.current;

    const updateWidth = () => {
      const parent = element.parentElement;
      if (!parent) return;

      const parentRect = parent.getBoundingClientRect();
      const parentScale = getComputedScale(parent);
      const parentWidth = parentRect.width * parentScale;

      const newWidth = offsetPx !== undefined ? parentWidth - offsetPx : parentWidth * (scaleFactor ?? 1);

      element.style.width = `${newWidth}px`;
    };

    const getComputedScale = (element: HTMLElement) => {
      const transform = window.getComputedStyle(element).transform;
      if (transform === "none") return 1;
      const matrix = new DOMMatrix(transform);
      return matrix.a;
    };

    const resizeObserver = new ResizeObserver(updateWidth);
    const parent = element.parentElement;
    if (parent) {
      resizeObserver.observe(parent);
    }

    window.addEventListener("resize", updateWidth);

    updateWidth();

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener("resize", updateWidth);
    };
  }, [elementRef, enabled, scaleFactor, offsetPx]);
}
